import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { PresenceRecord } from '@chancenwerk/db/PresenceRecord.js';
import { Course } from '@chancenwerk/db/courses/Course.js';
import { CourseIntensive } from '@chancenwerk/db/courses/CourseIntensive.js';
import { CourseLernfoerderung } from '@chancenwerk/db/courses/CourseLernfoerderung.js';
import { CourseFilter } from '@chancenwerk/db/filter/CourseFilter.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import '@chancenwerk/design/components/cr-date-picker-dropdown.js';
import '@chancenwerk/design/components/cr-dropdown-selection.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { combineLatest, of, switchMap } from 'rxjs';

const COURSE_TYPES = [CourseIntensive._class, CourseLernfoerderung._class];

/**
 * @customElement campus-page-administration-course-presence
 */
@customElement('campus-page-administration-course-presence')
export class CampusPageAdministrationCoursePresence extends ChemistryLitElement {
  @state() size = new WindowSizeController(this);

  @state() private readonly _currentDate = new RXController(this, DateTime.now());
  @state() private readonly _choosenCourseType = new RXController<string | null>(this, null);
  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());

  @state() private readonly _filter = new RXController(
    this,
    combineLatest([
      this._currentDate.observable,
      this._choosenCourseType.observable,
      this._currentUser.observable,
    ]).pipe(
      switchMap(([date, type, user]) => {
        return of(new CourseFilter({ courseType: type ?? '', date: date.toJSDate(), schuKoId: user?._id ?? '' }));
      }),
    ),
  );

  @state() private readonly _currentCourse = new RXController(
    this,
    this._filter.observable.pipe(
      switchMap(f => Course.subscribeCoursesByFilter(f ?? new CourseFilter({}))(Course.allFields)),
    ),
  );

  @state() private readonly _currentCoursePresenceRecord = new RXController(
    this,
    combineLatest([this._currentCourse.observable, this._currentDate.observable]).pipe(
      switchMap(([course, date]) =>
        PresenceRecord.getPresenceRecordForCourseAndDate(course?.at(0)?._id ?? '', date)(PresenceRecord.allFields),
      ),
    ),
  );

  override render() {
    // TODO change this permission
    return permissionGuard(ChancenPermission.campus.LOGIN_CAMPUS, () => this._content(), {
      forbiddenTemplate: html`<campus-page-no-permission></campus-page-no-permission>`,
      context: ANY_CONTEXT,
    });
  }

  private _content() {
    return html`
      <cr-flex padding="${this.size.mobile ? 'md' : 'lg'}" space="lg">
        <cr-flex space="md">
          <cr-h2> Anwesendheitskontrolle </cr-h2>
          <cr-text> Hier kannst du die Anwesendheit in dem jeweils ausgewählten Kurs eintragen </cr-text>
          <cr-flex horizontal space="md"> </cr-flex>
        </cr-flex>
        <cr-flex
          ${css({
            position: 'sticky',
            top: '0',
            zIndex: '1',
            background: this.colors.neutral.shade(50),
          })}
          padding="md"
          w100
        >
          <cr-flex
            horizontal
            crossaxis-center
            space="md"
            wrap
            ${css({
              background: this.colors.neutral.shade(50),
              paddingTop: this.spacing.md,
              paddingBottom: this.spacing.md,
            })}
          >
            <cr-date-picker-dropdown
              placeholder="Datum des Kurses"
              click-to-save
              flex
              clearable
              .value=${this._currentDate.value}
              @value-picked=${(e: ValueEvent<DateTime>) => {
                this._currentDate.next(e.detail.value);
              }}
            ></cr-date-picker-dropdown>
            <cr-dropdown-selection
              flex
              .value=${this._choosenCourseType.value}
              .placeholder=${'Kurstyp'}
              .selectables=${COURSE_TYPES}
              @value-picked=${async (e: ValueEvent<string>) => {
                this._choosenCourseType.next(e.detail.value);
              }}
            ></cr-dropdown-selection>
          </cr-flex>
        </cr-flex>

        <cr-flex space="md"> ${this._pageContent()} </cr-flex>
      </cr-flex>
    `;
  }

  private _pageContent() {
    return html` <cr-text>${this._currentCoursePresenceRecord.value?.date}</cr-text> `;
  }
}
